import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  getProject,
  updateProject,
  createProject,
} from "../../actions/projectActions";
import { getCollectiveLabourAgreements } from "../../actions/claActions";
import { getCompany, getCompanies } from "../../actions/companyActions";
import { getCompaniesOfEmployee } from "../../actions/employeeActions";

import Grid from "@material-ui/core/Grid";
import Form from "./Form";

import moment from "moment";
import InputElement from "../InputElement";
import {
  FormMode,
  InputElementSize,
  InputElementType,
  ReportRequiredByType,
} from "../../types/enums";

import { useTranslation } from "react-i18next";

const credentials = JSON.parse(localStorage.getItem("credentials"));

const ProjectForm = (props) => {
  const { t } = useTranslation();

  const [optionCompanies, setOptionCompanies] = useState([]);
  const [optionClas, setOptionClas] = useState([]);

  const [companyEmail, setCompanyEmail] = useState(" ");

  const [cla, setCla] = useState("");
  const [name, setName] = useState("");
  const [reportRequiredBy, setReportRequiredBy] = useState(false);
  const [contactPerson, setContactPerson] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [street, setStreet] = useState("");
  const [streetNumber, setStreetNumber] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [city, setCity] = useState("");
  const [beginDate, setBeginDate] = useState(moment());
  const [endDate, setEndDate] = useState(moment());

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const isCreateMode = props.mode === FormMode.CREATE;
  const isUpdateMode = props.mode === FormMode.UPDATE;
  const isViewMode = props.mode === FormMode.VIEW;

  const actionName = isCreateMode
    ? "toevoegen"
    : isUpdateMode
      ? "bijwerken"
      : "";

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const clas = await getCollectiveLabourAgreements();
      setOptionClas(clas);

      if (isCreateMode) {
        if (credentials.user.type === "COMPANY") {
          const company = await getCompany(credentials.user.email);

          setContactPerson(company.contactPerson);
          setEmail(company.email);
          setPhone(company.phone);
          setCompanyEmail(credentials.user.email);
        } else {
          const companies = credentials.user.isAdmin
            ? await props.getCompanies()
            : await props.getCompaniesOfEmployee(credentials.user.email, true);
          setOptionCompanies(companies);
        }
      }

      if (isUpdateMode || isViewMode) {
        const project = await props.getProject(props.projectId);

        const cla = isUpdateMode
          ? project.claId
          : clas.find((o) => o.id === project.claId).name;

        setCompanyEmail(project.companyEmail);
        setCla(cla);
        setName(project.name);
        setReportRequiredBy(project.reportRequiredBy);
        setContactPerson(project.contactPerson);
        setEmail(project.email);
        setPhone(project.phone);
        setStreet(project.street);
        setStreetNumber(project.streetNumber);
        setZipcode(project.zipcode);
        setCity(project.city);
        setBeginDate(project.beginDate);
        setEndDate(project.endDate);
      }
    } catch (error) {
      setErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    const form = {
      claId: cla,
      name,
      reportRequiredBy,
      contactPerson,
      email,
      phone,
      street,
      streetNumber,
      zipcode,
      city,
      beginDate,
      endDate,
      ...(isCreateMode && { companyEmail }),
    };

    const promise = isCreateMode
      ? props.createProject(form)
      : isUpdateMode
        ? props.updateProject(props.projectId, form)
        : null;

    promise
      .then(() => {
        if (isCreateMode) window.location.href = `/project`;
        setSuccessMessage(`Project ${actionName} is voltooid.`);
      })
      .catch(setErrorMessage)
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    (async () => {
      if (props.mode !== FormMode.CREATE) return;
      try {
        setIsLoading(true);
        const company = await getCompany(companyEmail);
        setContactPerson(company.contactPerson);
        setEmail(company.email);
        setPhone(company.phone);
        setCla(company.claId);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [companyEmail]);

  const inputs = [
    {
      id: "companyEmail",
      hidden: credentials.user.type === "COMPANY",
      label: "Bedrijf *",
      type: isCreateMode ? InputElementType.SELECT : InputElementType.TEXT,
      disabled: isUpdateMode,
      state: companyEmail,
      ...(isCreateMode && {
        setState: setCompanyEmail,
        options: optionCompanies.map((o) => ({ id: o.email, label: o.name })),
      }),
    },
    {
      id: "claId",
      label: "CAO",
      type: isViewMode ? InputElementType.TEXT : InputElementType.SELECT,
      state: cla,
      ...(!isViewMode && {
        setState: setCla,
        options: optionClas.map((o) => ({ id: o.id, label: o.name })),
      }),
    },
    {
      id: "name",
      type: InputElementType.TEXT,
      label: "Projectnaam *",
      state: name,
      ...(!isViewMode && {
        setState: setName,
      }),
    },
    {
      id: "reportRequiredBy",
      type: isViewMode ? InputElementType.TEXT : InputElementType.SELECT,
      label: "Rapportage verplicht door medewerkers",
      name: "reportRequiredBy",
      state: isViewMode ? t(reportRequiredBy) : reportRequiredBy,
      ...(!isViewMode && {
        setState: setReportRequiredBy,
        options: Object.values(ReportRequiredByType).map((o) => ({
          id: o,
          label: t(o),
        })),
      }),
    },
    {
      id: "contactPerson",
      type: InputElementType.TEXT,
      label: "Contactpersoon *",
      state: contactPerson,
      ...(!isViewMode && {
        setState: setContactPerson,
      }),
    },
    {
      id: "email",
      type: InputElementType.TEXT,
      label: "Email *",
      state: email,
      ...(!isViewMode && {
        setState: setEmail,
      }),
    },
    {
      id: "phone",
      type: InputElementType.TEXT,
      label: "Telefoonnummer *",
      state: phone,
      ...(!isViewMode && {
        setState: setPhone,
      }),
    },
    {
      id: "street",
      type: InputElementType.TEXT,
      size: InputElementSize.S,
      label: "Adres *",
      state: street,
      ...(!isViewMode && {
        setState: setStreet,
      }),
    },
    {
      id: "streetNumber",
      type: InputElementType.TEXT,
      size: InputElementSize.S,
      label: "Huisnr. *",
      state: streetNumber,
      ...(!isViewMode && {
        setState: setStreetNumber,
      }),
    },
    {
      id: "zipcode",
      type: InputElementType.TEXT,
      label: "Postcode *",
      state: zipcode,
      ...(!isViewMode && {
        setState: setZipcode,
      }),
    },
    {
      id: "city",
      type: InputElementType.TEXT,
      label: "Plaats *",
      state: city,
      ...(!isViewMode && {
        setState: setCity,
      }),
    },
    {
      id: "beginDate",
      label: "Begindatum *",
      type: InputElementType.DATE,
      state: beginDate,
      ...(!isViewMode && {
        setState: setBeginDate,
      }),
    },
    {
      id: "endDate",
      label: "Einddatum *",
      type: InputElementType.DATE,
      state: endDate,
      ...(!isViewMode && {
        setState: setEndDate,
      }),
    },
  ];

  return (
    <Form
      onSubmit={!isViewMode && handleSubmit}
      submitText={actionName}
      isFetching={isLoading}
      successMessage={successMessage}
      errorMessage={errorMessage}
      mode={props.mode}
    >
      <Grid container spacing={3}>
        {inputs.map((i) => (
          <InputElement key={i.id} item={i} />
        ))}
      </Grid>
    </Form>
  );
};

ProjectForm.propTypes = {
  companyEmail: PropTypes.string,
  projectId: PropTypes.string,

  getProject: PropTypes.func.isRequired,
  updateProject: PropTypes.func.isRequired,
  getCompanies: PropTypes.func.isRequired,
  getCompaniesOfEmployee: PropTypes.func.isRequired,
  createProject: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(Object.values(FormMode)).isRequired,
};

export default connect(null, {
  getProject,
  updateProject,
  getCompanies,
  createProject,
  getCompaniesOfEmployee,
})(ProjectForm);



// WEBPACK FOOTER //
// ./src/components/forms/ProjectForm.js